<template>
  <div class="language-levels">
    <Label v-if="showLabel" title="Language Level" />

    <!-- English row: language fixed, level required -->
    <div class="language-level-row">
      <div class="language-name">
        English<span class="required-mark">*</span>
      </div>
      <validation-provider
        v-slot="{ errors }"
        vid="languageLevelEn"
        slim="slim"
        name="English Level"
        rules="required"
      >
        <sui-form-field required :error="hasError(errors)">
          <sui-dropdown
            v-model="englishLevel"
            :placeholder="englishLevelPlaceholder"
            selection
            :options="levelOptions"
          />
          <ErrorMessage :message="errors[0]" />
        </sui-form-field>
      </validation-provider>
    </div>

    <!-- Additional language rows -->
    <div
      v-for="(row, index) in extraRows"
      :key="row.key"
      class="language-level-group"
    >
      <div class="language-level-row">
        <validation-provider
          v-slot="{ errors }"
          :vid="`language-${row.key}`"
          slim="slim"
          name="Language"
          rules="required"
        >
          <sui-form-field required :error="hasError(errors)">
            <sui-dropdown
              v-model="row.lang"
              placeholder="Select language"
              selection
              :options="availableLanguageOptions(row)"
            />
            <ErrorMessage :message="errors[0]" />
          </sui-form-field>
        </validation-provider>
        <validation-provider
          v-slot="{ errors }"
          :vid="`level-${row.key}`"
          slim="slim"
          name="Level"
          rules="required"
        >
          <sui-form-field required :error="hasError(errors)">
            <sui-dropdown
              v-model="row.level"
              placeholder="Select level"
              selection
              :options="levelOptions"
            />
            <ErrorMessage :message="errors[0]" />
          </sui-form-field>
        </validation-provider>
      </div>
      <div class="remove-wrapper">
        <span class="remove-row-btn" @click="removeRow(index)">Remove</span>
      </div>
    </div>

    <div class="add-wrapper">
      <span
        class="add-row-btn"
        :class="{ disabled: !canAddRow }"
        @click="addRow"
        >+ Add more</span
      >
    </div>

    <!-- Hidden inputs submitted to Rails as model[language_levels][lang]=level -->
    <input
      v-if="englishLevel"
      type="hidden"
      :name="`${model}[language_levels][en]`"
      :value="englishLevel"
    />
    <template v-for="row in extraRows">
      <input
        v-if="row.lang && row.level"
        :key="`hidden-${row.key}`"
        type="hidden"
        :name="`${model}[language_levels][${row.lang}]`"
        :value="row.level"
      />
    </template>
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate'
import Label from 'components/ui/form/Label.vue'
import ErrorMessage from 'components/ui/form/ErrorMessage.vue'

export default {
  name: 'LanguageLevels',

  components: {
    ValidationProvider,
    Label,
    ErrorMessage,
  },

  props: {
    // model name used to build input names, e.g. "talent" -> talent[language_levels][en]
    model: {
      type: String,
      required: true,
    },
    // [{ text: 'English', value: 'en' }, ...] (English excluded from picker below)
    languageOptions: {
      type: Array,
      default: () => [],
    },
    // [{ text: 'Native', value: 5 }, ...]
    levelOptions: {
      type: Array,
      default: () => [],
    },
    // initial language_levels hash for edit, e.g. { en: 5, th: 3 }
    value: {
      type: Object,
      default: () => ({}),
    },
    // hidden when the surrounding form already groups the rows under a heading
    showLabel: {
      type: Boolean,
      default: true,
    },
    // overridden on forms the candidate fills in about themselves
    englishLevelPlaceholder: {
      type: String,
      default: () => "Select candidate's English level",
    },
  },

  data() {
    const value = this.value || {}
    const extraRows = Object.keys(value)
      .filter((lang) => lang !== 'en')
      .map((lang, i) => ({ key: i, lang, level: value[lang] }))

    return {
      englishLevel: value.en || null,
      extraRows,
      uid: extraRows.length,
    }
  },

  computed: {
    // English (en) always takes one slot, so additional rows are capped at the
    // remaining languages.
    maxExtraRows() {
      return Math.max(0, this.languageOptions.length - 1)
    },
    canAddRow() {
      return this.extraRows.length < this.maxExtraRows
    },
  },

  methods: {
    hasError(errors) {
      return errors && errors.length > 0
    },

    // language options available for a row: exclude 'en' and languages already
    // picked in other rows (keep the row's own current selection).
    availableLanguageOptions(currentRow) {
      const taken = [
        'en',
        ...this.extraRows
          .filter((row) => row !== currentRow && row.lang)
          .map((row) => row.lang),
      ]
      return this.languageOptions.filter(
        (option) =>
          option.value === currentRow.lang || !taken.includes(option.value)
      )
    },

    addRow() {
      if (!this.canAddRow) return

      this.extraRows.push({ key: this.uid++, lang: null, level: null })
    },

    removeRow(index) {
      this.extraRows.splice(index, 1)
    },
  },
}
</script>

<style lang="scss" scoped>
@import 'stylesheets/global';

.language-levels {
  margin-bottom: 1em;
}

.language-level-row {
  align-items: flex-start;
  display: flex;
  gap: 1.5rem;
  margin-bottom: 0.75rem;

  // two equal columns filling the full width
  .language-name,
  .field {
    box-sizing: border-box;
    flex: 1 1 0;
    min-width: 0;
  }

  .language-name {
    padding-top: 0.7rem;
  }

  .field {
    /* stylelint-disable-next-line declaration-no-important */
    margin: 0 !important;
  }

  .ui.dropdown {
    /* stylelint-disable-next-line declaration-no-important */
    min-width: 0 !important;
    width: 100%;
  }

  @include sp {
    display: block;

    .language-name {
      margin-bottom: 0.5rem;
      padding-top: 0;
    }

    .field {
      /* stylelint-disable-next-line declaration-no-important */
      margin-bottom: 0.75rem !important;
    }
  }
}

// wraps one extra language row together with its "Remove" link
.language-level-group {
  margin-bottom: 0.75rem;

  .language-level-row {
    margin-bottom: 0.35rem;
  }
}

.required-mark {
  color: $required;
  margin-left: 0.2rem;
}

.remove-wrapper {
  display: flex;
  justify-content: flex-end;
}

.remove-row-btn {
  color: #db2828;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    opacity: 0.7;
  }
}

.add-wrapper {
  margin-top: 0.25rem;
}

.add-row-btn {
  color: $blue-primary;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    opacity: 0.7;
  }

  &.disabled {
    color: #ccc;
    cursor: not-allowed;
    pointer-events: none;
  }
}
</style>
